import { ConnectCall } from "@ameelio/connect-call-client";
import { Button, IconButton } from "@ameelio/ui";
import CloseIcon from "@mui/icons-material/Close";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import { Box, Grow, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Correspondent } from "../../api/graphql";
import ChatInput from "./ChatInput";
import ChatMessage from "./ChatMessage";

type Props = {
  currentUserId: string;
  correspondents: Pick<Correspondent, "id" | "fullName">[];
  questions: ConnectCall["webinarQuestions"];
  announcements: ConnectCall["webinarAnnouncements"];
  onSend?: (message: string) => void;
  onClose: () => void;
  muteSounds?: boolean;
  handRaised: boolean;
  raiseHand: () => Promise<void>;
  lowerHand: () => Promise<void>;
};

export default function WebinarQAndA({
  currentUserId,
  correspondents,
  questions,
  announcements,
  onSend,
  onClose,
  muteSounds,
  handRaised,
  raiseHand,
  lowerHand,
}: Props) {
  const { t } = useTranslation();

  // hand raising
  const [awaitingRaiseHand, setAwaitingRaiseHand] = useState(false);
  const [expectedRaiseHand, setExpectedRaiseHand] = useState(handRaised);
  useEffect(() => {
    if (awaitingRaiseHand && expectedRaiseHand === handRaised) {
      setAwaitingRaiseHand(false);
    }
  }, [awaitingRaiseHand, expectedRaiseHand, handRaised]);
  const toggleRaiseHand = useCallback(() => {
    if (handRaised) lowerHand();
    else raiseHand();

    setExpectedRaiseHand(!handRaised);
    setAwaitingRaiseHand(true);

    setTimeout(() => {
      setAwaitingRaiseHand(false);
    }, 2000);
  }, [raiseHand, lowerHand, handRaised]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: 1 }}>
      <Box sx={{ display: "flex", padding: 2 }}>
        <Typography variant="h1" component="div" sx={{ flexGrow: 1 }}>
          {t("Class")}
        </Typography>
        <IconButton size="small" ariaLabel={t("Close")} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          px: 2,
        }}
      >
        <Stack spacing={2}>
          {announcements.length > 0 && (
            <Stack spacing={2}>
              <Typography variant="h3">
                {t("Announcements")} ({announcements.length})
              </Typography>
              <Stack spacing={1}>
                {announcements.map((m) => {
                  // lookups are risky. i don't know how to avoid this one yet.
                  const correspondent = correspondents.find(
                    (p) => p.id === m.author.id
                  ) || {
                    id: "",
                    firstName: "",
                    lastName: "",
                    fullName: "",
                  };
                  return (
                    <Grow
                      in
                      timeout={500}
                      style={{ transformOrigin: "bottom center" }}
                      key={m.timestamp}
                    >
                      <Box
                        sx={[
                          {
                            display: "flex",
                          },
                          {},
                        ]}
                      >
                        <ChatMessage
                          currentUserId={currentUserId}
                          correspondent={correspondent}
                          contents={m.contents}
                          timestamp={new Date(m.timestamp)}
                          iconSlot={
                            <MessageOutlinedIcon
                              sx={{ height: 20, width: 20 }}
                            />
                          }
                        />
                      </Box>
                    </Grow>
                  );
                })}
              </Stack>
            </Stack>
          )}
          <Stack spacing={2}>
            <Typography variant="h3">
              {t("Questions ({{count}})", { count: questions.length })}
            </Typography>
            {questions.length ? (
              <Stack spacing={1}>
                {questions.map((m) => {
                  // lookups are risky. i don't know how to avoid this one yet.
                  const correspondent = correspondents.find(
                    (p) => p.id === m.author.id
                  ) || {
                    id: "",
                    firstName: "",
                    lastName: "",
                    fullName: "",
                  };
                  return (
                    <Grow
                      in
                      timeout={500}
                      style={{ transformOrigin: "bottom center" }}
                      key={m.timestamp}
                    >
                      <Box>
                        <ChatMessage
                          currentUserId={currentUserId}
                          correspondent={correspondent}
                          contents={m.contents}
                          timestamp={new Date(m.timestamp)}
                          iconSlot={
                            <MessageOutlinedIcon
                              sx={{ height: 20, width: 20 }}
                            />
                          }
                        />
                      </Box>
                    </Grow>
                  );
                })}
              </Stack>
            ) : null}
          </Stack>
          <ChatInput
            label={t("Write a question...")}
            onSend={onSend}
            muteSounds={muteSounds}
          />
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {t(
              "Prefer to talk with the teacher out loud? Other students will not hear you."
            )}
          </Typography>
          <Button
            onClick={toggleRaiseHand}
            variant={handRaised ? "contained" : "outlined"}
            disabled={awaitingRaiseHand}
          >
            {handRaised ? t("Lower hand") : t("Raise Hand")}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
